import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Checkbox, Spin, Alert, Row, Col, Select, message } from 'antd';
import { BASE_URL, BASIC_INFO_URL, PROFILE_INFO_URL } from '../../constants/Url'
import PhoneInput from '../InputField/PhoneInput';
import { useTranslation } from 'react-i18next';
import UploadAvata from '../UploadAvata';
import { useDispatch, useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { loginSuccessAction } from '../../modules/auth/actions';
import { storageKeys } from '../../constants/storage';
import { handleLogout } from '../../utils/storage';
import dataAddress from '../../utils/address.json';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const RegisterModal = forwardRef(({ onAuthSuccess, loading, handleCloses, setLoading, mode = 'phone', setView }, ref) => {
    const user = useSelector((state) => state.authReducer);
    let token = user && user?.token ? user?.token : null

    const [open, setOpen] = useState(false);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [loadingNew, setLoadingNew] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const [name, setName] = useState(user?.user?.name ?? null);
    const [gender, setGender] = useState(user?.user?.sex ?? null);
    const [province, setProvince] = useState(null);
    const [city, setCity] = useState(null);
    const [category, setCategory] = useState(user?.user?.category ?? null);
    const [isAgree, setIsAgree] = useState(false);
    const [error, setError] = useState(null);

    const [trialModalVisible, setTrialModalVisible] = useState(false);
    const [trialDays, setTrialDays] = useState(0);
    const [savedMemberData, setSavedMemberData] = useState(null);

    const { t } = useTranslation();
    const uploadRef = useRef()
    const dispatch = useDispatch();
    let provinceOptions = dataAddress?.map(item => ({ value: item.text, label: item.text }))
    let cityOptions = dataAddress?.find(item => item.text === province)?.children?.map(city => ({ value: city.text, label: city.text }))

    // Check if member already completed registration (e.g. via mobile App)
    useEffect(() => {
        if (token) {
            httpClient.get(PROFILE_INFO_URL).then(res => {
                const member = res?.data?.member || res?.data?.data;
                if (member?.completedTS) {
                    saveAuth(token, member, res?.data?.refreshToken);
                } else if (member) {
                    const existingUser = user?.user || {};
                    const mergedUser = {
                        ...existingUser,
                        ...member,
                        email: member.email || existingUser.email || '',
                        phone: member.phone || existingUser.phone || null
                    };
                    dispatch(loginSuccessAction({ token, user: mergedUser, photo: mergedUser?.picUrl }));
                    localStorage.setItem(storageKeys.USER, JSON.stringify(mergedUser));
                    if (member.name && !name) setName(member.name);
                    if (member.sex && !gender) setGender(member.sex);
                    if (member.category && !category) setCategory(member.category);
                    if (member.province && !province) setProvince(member.province);
                    if (member.city && !city) setCity(member.city);
                }
            }).catch(err => {
                console.error("Failed to check profile in RegisterModal:", err);
            });
        }
    }, [token]);

    const handleClose = () => {
        setOpen(false);
        setError(null);
        setLoading(false);
    };

    const handleShow = () => {
        setOpen(true);
    };

    useImperativeHandle(ref, () => ({
        open: handleShow,
        close: handleClose
    }));

    const saveAuth = (token, newUser, refreshToken) => {
        const existingUser = user?.user || {};
        const mergedUser = {
            ...existingUser,
            ...newUser,
            email: newUser?.email || existingUser?.email || '',
            phone: newUser?.phone || existingUser?.phone || null
        };
        dispatch(loginSuccessAction({ token, user: mergedUser, photo: mergedUser?.picUrl }));
        if (token) localStorage.setItem(storageKeys.TOKEN, btoa(token));
        localStorage.setItem(storageKeys.USER, JSON.stringify(mergedUser));
        handleClose();
        handleCloses && handleCloses(true);
    };

    const getDataInfo = async (tkn, mem, refToken) => {
        try {
            const res = await httpClient.get(PROFILE_INFO_URL)
                .then(res => res.data)
                .catch(error => ({ status: 500, error }));
            const currentMember = res?.member || res?.data || mem;
            let days = 0;
            if (currentMember?.memberType === 'trial' && currentMember?.expiredTS) {
                const diff = dayjs.utc(currentMember.expiredTS).startOf('day').diff(dayjs.utc().startOf('day'), 'day');
                if (diff > 0) days = diff;
            } else if (currentMember?.trialDays > 0) {
                days = currentMember.trialDays;
            } else if (currentMember?.expiredTS && currentMember?.memberType !== 'normal') {
                const diff = dayjs.utc(currentMember.expiredTS).startOf('day').diff(dayjs.utc().startOf('day'), 'day');
                if (diff > 0) days = diff;
            }

            if (days > 0) {
                setTrialDays(days);
                setSavedMemberData({ token: tkn || token, member: currentMember, refreshToken: refToken || res?.refreshToken });
                setTrialModalVisible(true);
            } else {
                saveAuth(tkn || token, currentMember, refToken || res?.refreshToken);
            }
        } catch (e) {
            saveAuth(tkn || token, mem, refToken);
        }
    }

    const handleCloseTrialModal = () => {
        setTrialModalVisible(false);
        if (savedMemberData) {
            saveAuth(savedMemberData.token, savedMemberData.member, savedMemberData.refreshToken);
        } else {
            handleClose();
            handleCloses && handleCloses(true);
        }
    };

    const handleSendCode = async () => {
        if (!email) {
            message.error(t("text_enter_email"));
            return;
        }
        setLoadingNew(true);
        try {
            const res = await httpClient.post('/auth/request-otp-mail', { email })
                .then(res => res.data)
                .catch(error => ({ status: 500, error }));
            
            if (res?.status === 200 || res?.status === 201 || res?.code === 200 || res?.message === 'success' || !res?.error) {
                message.success(t("text_code_sent"));
                setCountdown(60);
                const timer = setInterval(() => {
                    setCountdown((prev) => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                message.error(t(res?.error?.message || res?.message || "Failed to send code"));
            }
        } catch (error) {
            message.error(t("Failed to send code"));
        } finally {
            setLoadingNew(false);
        }
    };

    const handlePhoneContinue = async (e) => {
        e.preventDefault();

        if (!category || !name?.trim() || !gender || !city || !province) {
            const emptyMsg = t("Please fill in all required fields");
            message.error(emptyMsg && emptyMsg !== "Please fill in all required fields" ? emptyMsg : "请填写所有必填项");
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const res = await httpClient.put(BASIC_INFO_URL, {
                "name": name.trim(),
                "sex": gender, // Male or Female
                "category": category, // Customer or Professional Stylist
                "city": city,
                "province": province
            })
                .then(res => res.data)
                .catch(error => ({ status: 500, error }));
                console.log("res", res)
            if (res?.status === 200) {
                await getDataInfo(token, res?.member, res?.refreshToken);
            } else {
                setError(t(res?.error?.message || res?.message || "Failed to login"));
            }
            setLoading(false);
        } catch (error) {
            setError(t("Failed to login"));
            setLoading(false);
        }
    };

    return (
        // <Spin spinning={true}>
        <div style={{ padding: 20 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <p className="labelLogin">
                    {t("User Infor")}
                </p>
            </div>
            {/* <div className='' style={{display:"flex", justifyContent:'center', paddingBottom:"20px"}}>
                <UploadAvata t={t}/>
            </div> */}

            <Form layout="vertical">
                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={t("MemberID")}
                            name="memberID"
                        >
                            <p>{user?.user?.memberId ?? ""}</p>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        {user?.user?.email && (!user?.user?.phone?.number || mode === 'email') ? (
                            <Form.Item
                                label={t("text_email") || "邮箱"}
                                name="emailText"
                            >
                                <p>{user?.user?.email ?? ""}</p>
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label={t("Phone Number") || "手机号码"}
                                name="phone"
                            >
                                <p>{user?.user?.phone?.countryCode ? `+${user?.user?.phone?.countryCode}` : ""} {user?.user?.phone?.number ?? ""}</p>
                            </Form.Item>
                        )}
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={<><span style={{ color: '#e80d44', marginRight: 4 }}>*</span>{t("Name")}</>}
                            name="name"
                        >
                            <Input size='large' value={name} 
                            placeholder={t("text_enter_name")}
                            onChange={(e) => setName(e.target.value)} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            label={<><span style={{ color: '#e80d44', marginRight: 4 }}>*</span>{t("gender")}</>}
                            name="gender"
                        >
                            <Select
                                size='large'
                                value={gender}
                                placeholder={t("text_select_gender")}
                                onChange={(value) => setGender(value)}
                                options={[
                                    { value: 'Male', label: t('Male') },
                                    { value: 'Female', label: t('Female') },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={<><span style={{ color: '#e80d44', marginRight: 4 }}>*</span>{t("Province")}</>}
                        >
                            <Select
                                size='large'
                                value={province}
                                placeholder={t("text_select_province")}
                                onChange={(value) => {
                                    setProvince(value);
                                    setCity(null); 
                                }}
                                options={provinceOptions}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            label={<><span style={{ color: '#e80d44', marginRight: 4 }}>*</span>{t("City")}</>}
                        >
                            <Select
                                size='large'
                                value={city}
                                placeholder={t("text_select_city")}
                                onChange={(value) => setCity(value)}
                                options={cityOptions}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={12}>
                    <Col xs={24} md={24}>
                        <Form.Item
                            label={<><span style={{ color: '#e80d44', marginRight: 4 }}>*</span>{t("Category")}</>}
                            name="category"
                        >
                            <Select
                                size='large'
                                value={category}
                                onChange={(value) => setCategory(value)}
                                placeholder={t("text_select_category")}
                                options={[
                                    { value: 'Customer', label: t('Customer') },
                                    { value: 'Professional Stylist', label: t('Professional Stylist')},
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Button
                    type="primary"
                    size='large'
                    block
                    onClick={(e) => handlePhoneContinue(e)}
                >
                    {loading ? <Spin size="small" /> : t('Save')}
                </Button>

                <div style={{ textAlign: 'center', marginTop: 14 }}>
                    <button
                        type="button"
                        onClick={() => {
                            handleLogout();
                            handleClose();
                            handleCloses && handleCloses();
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#999',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#e80d44'}
                        onMouseLeave={(e) => e.target.style.color = '#999'}
                    >
                        {t("text_login")}
                    </button>
                </div>
            </Form>

            {/* Free Trial Popup Modal */}
            <Modal
                open={trialModalVisible && trialDays > 0}
                onCancel={handleCloseTrialModal}
                footer={null}
                centered
                closable={true}
                width={400}
                styles={{ body: { padding: '32px 24px', textAlign: 'center' } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: '#fff0f4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 16,
                        color: '#e80d44'
                    }}>
                        <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 12 20 22 4 22 4 12" />
                            <rect x="2" y="7" width="20" height="5" />
                            <line x1="12" y1="22" x2="12" y2="7" />
                            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                        </svg>
                    </div>

                    {/* <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: 12 }}>
                        {t("text_registration_success") || "注册成功"}
                    </h3> */}

                    <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: 24, lineHeight: '1.6' }}>
                        首次注册您将获得{trialDays}天的免费试用期。
                    </p>

                    <Button
                        type="primary"
                        size="large"
                        block
                        onClick={handleCloseTrialModal}
                        style={{
                            background: 'linear-gradient(to right, #e80d44, #ff3b68)',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: 600,
                            height: '44px',
                            fontSize: '15px'
                        }}
                    >
                        {t("text_i_know")}
                    </Button>
                </div>
            </Modal>
        </div>
        // </Spin>
    );
});

export default RegisterModal;